import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  TextField,
  FormControlLabel,
  Checkbox,
  InputAdornment,
} from "@mui/material";
import { validateAcknowledgementField } from "./acknowledgements.helper";

const Acknowledgements = ({
  data,
  onDataChange,
  onValidationChange,
  showErrors,
  consentHeader,
  consentDetail,
}) => {
  const [formData, setFormData] = useState(() => ({
    attested: data?.attested || false,
    advocateName: data?.advocateName || "",
    advocateEmail: data?.advocateEmail || "",
    advocatePhone: data?.advocatePhone || "",
  }));

  useEffect(() => {
    if (data) {
      setFormData((prev) => ({
        attested: data.attested !== undefined ? data.attested : prev.attested,
        advocateName:
          data.advocateName !== undefined
            ? data.advocateName
            : prev.advocateName,
        advocateEmail:
          data.advocateEmail !== undefined
            ? data.advocateEmail
            : prev.advocateEmail,
        advocatePhone:
          data.advocatePhone !== undefined
            ? data.advocatePhone
            : prev.advocatePhone,
      }));
    }
  }, [data]);

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  useEffect(() => {
    // Initial validation
    const fields = [
      "attested",
      "advocateName",
      "advocateEmail",
      "advocatePhone",
    ];
    const initialErrors = {};
    fields.forEach((field) => {
      const error = validateAcknowledgementField(field, formData[field]);
      if (error) initialErrors[field] = error;
    });
    setErrors(initialErrors);
  }, [formData]);

  const validateField = (name, value) => {
    const error = validateAcknowledgementField(name, value);
    setErrors((prev) => {
      const newErrors = { ...prev };
      if (error) {
        newErrors[name] = error;
      } else {
        delete newErrors[name];
      }
      return newErrors;
    });
  };

  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(Object.keys(errors).length === 0);
    }
  }, [errors, onValidationChange]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const finalValue = type === "checkbox" ? checked : value;
    setFormData((prev) => {
      const next = { ...prev, [name]: finalValue };
      onDataChange(next);
      return next;
    });
    validateField(name, finalValue);
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
  };

  const getErrorProps = (name) => {
    const isInvalid = !!errors[name];
    const shouldShow = touched[name] || showErrors;
    return {
      error: isInvalid && shouldShow,
      helperText: shouldShow ? errors[name] : "",
    };
  };
  return (
    <Box
      sx={{
        backgroundColor: "#FFFFFF",
        borderRadius: "12px",
        p: 2,
        border: "1px solid #E8E8E9",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 3,
      }}
    >
      <Typography
        sx={{ fontWeight: 600, fontSize: "16px", color: "#403C6F", mb: "-8px" }}
      >
        Acknowledgements
      </Typography>

      {consentDetail && (
        <Box
          sx={{
            backgroundColor: "#F9F9F9",
            borderRadius: "8px",
            p: 2,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Typography
            sx={{ fontSize: "13px", color: "#000000", fontWeight: 400 }}
          >
            {consentDetail}
          </Typography>
        </Box>
      )}

      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <FormControlLabel
          control={
            <Checkbox
              name="attested"
              size="small"
              checked={formData.attested}
              onChange={handleChange}
              sx={{
                "&.Mui-checked": { color: "#403C6F !important" },
                color: getErrorProps("attested").error
                  ? "#d32f2f !important"
                  : "#D8D6E2",
                p: 0.5,
                mr: 1,
                ml: 1,
              }}
            />
          }
          label={
            <Typography
              sx={{
                fontSize: "13px",
                color: getErrorProps("attested").error ? "#d32f2f" : "#4B4B4B",
                fontWeight: 500,
                lineHeight: 1.4,
              }}
            >
              {consentHeader ||
                "I have confirmed the member has agreed to have the above medication shipped under the applicable laws of the program offered by RxManage."}
              <span style={{ color: "#E53935", marginLeft: "4px" }}>*</span>
            </Typography>
          }
          sx={{ m: 0, alignItems: "center" }}
        />
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1 }}>
        <Typography
          sx={{ fontSize: "13px", color: "#403C6F", fontWeight: 500 }}
        >
          Name Of Authorized Patient Care Advocate
        </Typography>
        <TextField
          name="advocateName"
          placeholder="Enter advocate name"
          variant="outlined"
          size="small"
          value={formData.advocateName}
          onChange={(e) => {
            if (e.target.value.length <= 300) handleChange(e);
          }}
          onBlur={handleBlur}
          {...getErrorProps("advocateName")}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <Typography
                    component="span"
                    sx={{
                      fontSize: "12px",
                      color: "#666",
                      whiteSpace: "nowrap",
                      mr: 0.5,
                    }}
                  >
                    {(formData.advocateName || "").length}/300
                  </Typography>
                </InputAdornment>
              ),
            },
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: "#FFFFFF",
              borderRadius: "8px",
              "& fieldset": {
                borderColor: getErrorProps("advocateName").error
                  ? "#d32f2f"
                  : "#E8E8E9",
              },
            },
            "& .MuiInputBase-input": {
              fontSize: "14px",
              py: 1.5,
              color: "#0e1324",
              pr: 0,
            },
          }}
        />
      </Box>

      <Box sx={{ display: "flex", gap: 2 }}>
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography
            sx={{ fontSize: "13px", color: "#403C6F", fontWeight: 500 }}
          >
            Email Address
          </Typography>
          <TextField
            name="advocateEmail"
            placeholder="Enter email address"
            variant="outlined"
            size="small"
            value={formData.advocateEmail}
            onChange={(e) => {
              if (e.target.value.length <= 74) handleChange(e);
            }}
            onBlur={handleBlur}
            {...getErrorProps("advocateEmail")}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography
                      component="span"
                      sx={{
                        fontSize: "12px",
                        color: "#666",
                        whiteSpace: "nowrap",
                        mr: 0.5,
                      }}
                    >
                      {(formData.advocateEmail || "").length}/74
                    </Typography>
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#FFFFFF",
                borderRadius: "8px",
                "& fieldset": {
                  borderColor: getErrorProps("advocateEmail").error
                    ? "#d32f2f"
                    : "#E8E8E9",
                },
              },
              "& .MuiInputBase-input": {
                fontSize: "14px",
                py: 1.5,
                color: "#0e1324",
                pr: 0,
              },
            }}
          />
        </Box>
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1 }}>
          <Typography
            sx={{ fontSize: "13px", color: "#403C6F", fontWeight: 500 }}
          >
            Phone Number
          </Typography>
          <TextField
            name="advocatePhone"
            placeholder="Enter phone number"
            variant="outlined"
            size="small"
            value={formData.advocatePhone}
            onChange={(e) => {
              if (e.target.value.length <= 10) handleChange(e);
            }}
            onBlur={handleBlur}
            {...getErrorProps("advocatePhone")}
            slotProps={{
              input: {
                endAdornment: (
                  <InputAdornment position="end">
                    <Typography
                      component="span"
                      sx={{
                        fontSize: "12px",
                        color: "#666",
                        whiteSpace: "nowrap",
                        mr: 0.5,
                      }}
                    >
                      {(formData.advocatePhone || "").length}/10
                    </Typography>
                  </InputAdornment>
                ),
              },
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "#FFFFFF",
                borderRadius: "8px",
                "& fieldset": {
                  borderColor: getErrorProps("advocatePhone").error
                    ? "#d32f2f"
                    : "#E8E8E9",
                },
              },
              "& .MuiInputBase-input": {
                fontSize: "14px",
                py: 1.5,
                color: "#0e1324",
                pr: 0,
              },
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

Acknowledgements.propTypes = {
  data: PropTypes.object.isRequired,
  onDataChange: PropTypes.func.isRequired,
  onValidationChange: PropTypes.func,
  showErrors: PropTypes.bool,
  consentHeader: PropTypes.string,
  consentDetail: PropTypes.string,
};

export default Acknowledgements;
